import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart, Star, ArrowRight, Quote, Sparkles, CheckCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessStories = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [storyText, setStoryText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eligiblePosts, setEligiblePosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState('');

  useEffect(() => {
    fetchStories();
    if (isAuthenticated) fetchEligiblePosts();
  }, [isAuthenticated]);

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/success-stories`);
      setStories(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchEligiblePosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/success-stories/eligible`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligiblePosts(res.data);
    } catch {}
  };

  const handleSubmit = async () => {
    if (!storyText.trim() || !selectedPost) {
      toast.error(language === 'it' ? 'Compila tutti i campi' : 'Fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/success-stories`, {
        post_id: selectedPost,
        story: storyText.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(language === 'it' ? 'Storia inviata! Sarà visibile dopo la revisione.' : 'Story submitted! It will be visible after review.');
      setShowSubmit(false);
      setStoryText('');
      setSelectedPost('');
      fetchStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl p-8 md:p-12 mb-10 text-white relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-400/20 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative z-10 max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-7 w-7" />
              <span className="font-medium text-amber-100">
                {language === 'it' ? 'Storie di Successo' : 'Success Stories'}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-testid="success-stories-title">
              {language === 'it'
                ? 'Le donazioni che hanno cambiato vite'
                : 'Donations that changed lives'}
            </h1>
            <p className="text-amber-100 text-lg mb-6">
              {language === 'it'
                ? 'Scopri come la generosità della nostra community ha fatto la differenza. Ogni storia è una testimonianza reale.'
                : 'Discover how our community\'s generosity made a difference. Every story is a real testimony.'}
            </p>
            {isAuthenticated && eligiblePosts.length > 0 && (
              <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-amber-700 hover:bg-amber-50 rounded-full px-6 font-semibold shadow-lg" data-testid="share-success-story-btn">
                    <Heart className="h-4 w-4 mr-2" fill="currentColor" />
                    {language === 'it' ? 'Condividi la tua storia' : 'Share your story'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                      {language === 'it' ? 'Racconta il tuo successo' : 'Tell your success story'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-stone-700">
                        {language === 'it' ? 'Seleziona la richiesta' : 'Select the request'}
                      </label>
                      <select
                        value={selectedPost}
                        onChange={(e) => setSelectedPost(e.target.value)}
                        className="w-full h-12 rounded-xl border border-stone-200 px-3 text-sm"
                        data-testid="success-story-post-select"
                      >
                        <option value="">{language === 'it' ? 'Scegli...' : 'Choose...'}</option>
                        {eligiblePosts.map(p => (
                          <option key={p.post_id} value={p.post_id}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                    <Textarea
                      data-testid="success-story-text"
                      placeholder={language === 'it'
                        ? 'Racconta come le donazioni hanno cambiato la tua vita...'
                        : 'Tell how donations changed your life...'}
                      className="min-h-[150px] rounded-xl"
                      value={storyText}
                      onChange={(e) => setStoryText(e.target.value)}
                    />
                    <Button
                      data-testid="submit-success-story-btn"
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold"
                    >
                      {submitting ? '...' : (language === 'it' ? 'Invia la tua storia' : 'Submit your story')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </motion.div>

        {/* Stories List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-20">
            <Star className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 mb-2" data-testid="no-stories-message">
              {language === 'it' ? 'Ancora nessuna storia' : 'No stories yet'}
            </h3>
            <p className="text-stone-500 mb-6 max-w-md mx-auto">
              {language === 'it'
                ? 'Sii il primo a condividere come le donazioni hanno cambiato la tua vita!'
                : 'Be the first to share how donations changed your life!'}
            </p>
            <Link to="/feed">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6">
                {language === 'it' ? 'Esplora le richieste' : 'Explore requests'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {stories.map((story, index) => (
              <motion.div
                key={story.story_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-8"
                data-testid={`success-story-${story.story_id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage src={story.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${story.user_id}`} />
                    <AvatarFallback className="bg-amber-100 text-amber-700">{story.user_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/profile/${story.user_id}`} className="font-semibold text-stone-900 hover:text-amber-700 transition-colors">
                        {story.user_name}
                      </Link>
                      {story.is_verified && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {language === 'it' ? 'Verificato' : 'Verified'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500">
                      {new Date(story.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <Quote className="h-8 w-8 text-amber-200 shrink-0" />
                </div>

                {story.post_title && (
                  <Link to={`/posts/${story.post_id}`} className="block mb-4">
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 hover:border-amber-300 transition-colors">
                      <p className="text-sm font-medium text-amber-800">
                        {language === 'it' ? 'Richiesta originale:' : 'Original request:'} {story.post_title}
                      </p>
                      {story.amount_raised > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {language === 'it' ? 'Raccolti:' : 'Raised:'} {story.currency || '€'}{story.amount_raised.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>
                )}

                <p className="text-stone-700 leading-relaxed">{story.story}</p>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SuccessStories;
